import React from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import * as Interfaces from 'interfaces';
import { constants, logger } from 'utils';
import { Step as Done } from '../Done';
import { Step as Assess } from '../Assess';
import { permitService } from 'services';
import { DefaultErrorResponse } from 'utils/constants';
import { AxiosError } from 'axios';

type Values = Partial<Interfaces.Common.FormValues>;

const initialValues: Values = {
  consent: false
};
const initialErrors: FormikErrors<Pick<Interfaces.Common.FormValues, 'consent'>> = {};
const initialTouched: FormikTouched<Pick<Interfaces.Common.FormValues, 'consent'>> = {
  consent: false
};

// const wizardSchema = Yup.bool().required().oneOf([true], 'Please tick this box to proceed.');
const wizardSchema = {
  consent: Yup.mixed().test(
    'isTicked',
    'Please tick this box to proceed.',
    function validate(value: boolean) {
      return value;
    }
  )
};

const validationSchema = Yup.object().shape<Values>(wizardSchema) as Yup.Schema<Values>;

const Component = React.lazy(() => import('./Review'));

const Step: StepProps<Values> = {
  id: 'review',
  title: constants.Page.title,
  stepName: 'Review',
  Component,
  validation: {
    schema: validationSchema,
    fieldNames: ['consent']
  },
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: (wizardContext, form, appStates) => {
          const appContext = appStates[constants.StorageKeys.State] as Interfaces.Store.AppContext;
          appContext.actions.updateError();
          wizardContext.dispatch({
            type: 'GOTO_STEP',
            stepId: Assess.id,
            values: form.values
          });
        }
      },
      {
        content: 'Submit',
        shouldValidate: true,
        isSubmit: true,
        type: 'primary',
        onClick: async (context, form, appStates) => {
          const service = new permitService();
          const appContext = appStates[constants.StorageKeys.State] as Interfaces.Store.AppContext;
          try {
            form.setSubmitting(true);
            const result = await service.createPermit(
              form.values as Interfaces.Common.FormValues,
              appContext.currentState.transactionID
            );

            // Note - typo in message is intentional, it's a typo on the BE too.
            // Throw an error if it fails - if it fails, it still sends a 200. Need to make sure it's not a failure.
            if (result.data.messge === 'failure') {
              throw new Error('Submission failed');
            }

            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Done.id,
              values: {
                applicationId: result.data.applicationId
              }
            });
          } catch (e: any) {
            // Backend is gone - redirect home
            if (e.isAxiosError && (e as AxiosError).response?.status === 410) {
              window.location.href = process.env.PUBLIC_URL || '/';
            } else {
              logger.error('Submit error', e);
              appContext.actions.updateError({
                title: DefaultErrorResponse.title,
                value: 'Please try again later.'
              });
            }
          }
          form.setSubmitting(false);
        }
      }
    ]
  }
};

export { Step, initialValues, initialTouched, initialErrors, wizardSchema };
